import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { collection, addDoc, doc, getDoc, Timestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { calculateStayBreakdown } from '../lib/pricing';
import { useAuth } from '../contexts/AuthContext';
import type { Listing } from '@shared/types';
import toast from 'react-hot-toast';

export default function BookingCheckout() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [, setBookingId] = useState<string | null>(null);

  const checkIn = searchParams.get('checkIn');
  const checkOut = searchParams.get('checkOut');

  useEffect(() => {
    if (!currentUser) {
      toast.error('Please log in to make a booking');
      navigate('/login');
      return;
    }

    if (!id || !checkIn || !checkOut) {
      toast.error('Missing booking information');
      navigate('/');
      return;
    }

    // Create booking directly in Firestore
    const createBooking = async () => {
      setLoading(true);
      try {
        // Get listing to calculate price
        const listingRef = doc(db, 'listings', id!);
        const listingSnap = await getDoc(listingRef);
        
        if (!listingSnap.exists()) {
          throw new Error('Listing not found');
        }

        const listing = listingSnap.data() as Listing;
        const checkInDate = new Date(checkIn!);
        const checkOutDate = new Date(checkOut!);

        const breakdown = calculateStayBreakdown(listing, checkInDate, checkOutDate);
        const { nights, total } = breakdown;

        // Check for overlapping bookings
        const bookingsQuery = query(
          collection(db, 'bookings'),
          where('listingId', '==', id),
          where('status', '==', 'confirmed')
        );
        const bookingsSnapshot = await getDocs(bookingsQuery);
        
        const hasOverlap = bookingsSnapshot.docs.some((doc) => {
          const booking = doc.data();
          const bookingCheckIn = booking.checkinTimestamp?.toDate
            ? booking.checkinTimestamp.toDate()
            : new Date(booking.checkinTimestamp);
          const bookingCheckOut = booking.checkoutTimestamp?.toDate
            ? booking.checkoutTimestamp.toDate()
            : new Date(booking.checkoutTimestamp);

          return (
            (checkInDate >= bookingCheckIn && checkInDate < bookingCheckOut) ||
            (checkOutDate > bookingCheckIn && checkOutDate <= bookingCheckOut) ||
            (checkInDate <= bookingCheckIn && checkOutDate >= bookingCheckOut)
          );
        });

        if (hasOverlap) {
          throw new Error('Dates are no longer available');
        }

        // Create booking
        const bookingsRef = collection(db, 'bookings');
        const bookingData = {
          listingId: id,
          hostId: listing.hostId,
          guestId: currentUser.uid,
          checkinTimestamp: Timestamp.fromDate(checkInDate),
          checkoutTimestamp: Timestamp.fromDate(checkOutDate),
          nights,
          totalCents: total,
          currency: 'ZAR',
          status: 'pending',
          payment: {
            amount: total,
            currency: 'ZAR',
            provider: 'payfast',
            captureStatus: 'pending',
          },
          cancellationPolicy: 'moderate',
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };

        const docRef = await addDoc(bookingsRef, bookingData);
        setBookingId(docRef.id);
        toast.success('Request received – we will get back to you within 48 hours.');
        navigate(`/booking/${docRef.id}/confirmation`);
      } catch (error: any) {
        toast.error(error.message || 'Error creating booking');
        navigate(`/listing/${id}`);
      } finally {
        setLoading(false);
      }
    };

    createBooking();
  }, [id, checkIn, checkOut, navigate, currentUser]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32373c]"></div>
        </div>
        <p className="text-center text-gray-600 mt-4">Creating your booking...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-6">Complete Your Booking</h1>
      <div className="card">
        <p className="text-gray-600">Processing your booking...</p>
      </div>
    </div>
  );
}
